/**
 * HelpArticlePage — single FAQ article view.
 *
 * Renders the article body as plain text wrapped in `<pre>`-style typography
 * (we deliberately do not parse markdown here — keeping rendering server-safe
 * and trivially injection-proof. A future iteration can swap in a markdown
 * renderer once content guidelines + sanitisation are in place).
 */
import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Icons } from '@/src/modules/components/Icons'
import { cn } from '@/src/infrastructure/lib/utils'
import { getFaqBySlug, voteOnFaq, type ApiFaqArticle } from '@/src/infrastructure/lib/api'
import { FAQ_CATEGORY_LABEL } from './constants'

export function HelpArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const [article, setArticle] = useState<ApiFaqArticle | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [vote, setVote] = useState<'up' | 'down' | null>(null)

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setLoading(true)
    setError('')
    getFaqBySlug(slug)
      .then((a) => !cancelled && setArticle(a))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [slug])

  async function castVote(helpful: boolean) {
    if (!article || vote) return
    setVote(helpful ? 'up' : 'down')
    try {
      const updated = await voteOnFaq(article.id, helpful)
      setArticle(updated)
    } catch {
      setVote(null)
    }
  }

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 max-w-3xl mx-auto space-y-6">
      <Link
        to="/dashboard/help"
        className="inline-flex items-center gap-1 text-xs font-semibold text-on-surface-variant hover:text-on-surface"
      >
        <Icons.ChevronLeft size={14} /> Back to help center
      </Link>

      {loading && <p className="text-sm text-on-surface-variant">Loading article…</p>}
      {error && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {article && (
        <article className="rounded-3xl border border-outline-variant/40 bg-surface-low/40 p-6 md:p-10 space-y-6">
          <header className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              {FAQ_CATEGORY_LABEL[article.category]}
            </p>
            <h1 className="text-2xl md:text-3xl font-bold text-on-surface">{article.title}</h1>
            {article.excerpt && (
              <p className="text-sm text-on-surface-variant">{article.excerpt}</p>
            )}
          </header>

          <div className="prose prose-sm md:prose-base max-w-none whitespace-pre-wrap text-on-surface">
            {article.content}
          </div>

          {article.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-surface-low border border-outline-variant/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-on-surface-variant"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Helpfulness vote */}
          <footer className="border-t border-outline-variant/30 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-on-surface-variant">Was this article helpful?</p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!!vote}
                onClick={() => castVote(true)}
                className={cn(
                  'rounded-full border px-4 py-1 text-xs font-semibold transition-colors',
                  vote === 'up'
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700'
                    : 'border-outline-variant/40 text-on-surface-variant hover:border-emerald-500/40',
                )}
              >
                👍 Yes ({article.helpfulCount})
              </button>
              <button
                type="button"
                disabled={!!vote}
                onClick={() => castVote(false)}
                className={cn(
                  'rounded-full border px-4 py-1 text-xs font-semibold transition-colors',
                  vote === 'down'
                    ? 'border-rose-500 bg-rose-500/10 text-rose-700'
                    : 'border-outline-variant/40 text-on-surface-variant hover:border-rose-500/40',
                )}
              >
                👎 No ({article.notHelpfulCount})
              </button>
            </div>
          </footer>
        </article>
      )}

      <div className="rounded-2xl border border-dashed border-outline-variant/40 p-4 text-center">
        <p className="text-sm text-on-surface-variant">
          Still need help?{' '}
          <Link
            to="/dashboard/help/tickets/new"
            className="font-semibold text-primary hover:underline"
          >
            Contact support →
          </Link>
        </p>
      </div>
    </div>
  )
}

